import { useEffect, useState } from 'react'
import { Modal, Popover, SegmentedControl, Text, TextInput, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  IconCheck,
  IconCoin,
  IconCopy,
  IconDownload,
  IconLink,
  IconMail,
  IconMessage,
  IconReceiptRefund,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react'
import { QRCodeSVG } from 'qrcode.react'
import { useCopay } from '@/hooks/useCopay'
import { EOB_RESPONSIBILITY_KINDS, type PatientInvoiceKind } from '@/model/middleware-types'
import { PATIENT_INVOICE_KIND_LABELS } from '@/model/patient-invoice-kind'
import { formatDateTime, formatPhoneInput, isPhoneReady, normalizePhoneToE164 } from '@/utils'
import { ActionButton, CardBadge, CardSkeleton, DetailCard } from './detailCardPrimitives'

// The one patient-payment card: give it an encounterId and it collects the visit's
// co-pay, deductible or co-insurance (Stripe checkout, payment link by QR / email /
// SMS) and shows what was already collected (receipts, refund). Mounted wherever an
// encounter is in scope — the schedule's appointment preview and the appointment
// details modal — so the flow is the same everywhere.
type Props = {
  encounterId: string | null
  /** The appointment carries the self-pay marker: Visit Payment, self-pay kind first. */
  selfPay?: boolean
}

function dollars(cents?: number): string {
  return cents != null ? `$${(cents / 100).toFixed(2)}` : ''
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

const ICON_BUTTON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E7E9EC] text-[#6B7280] hover:bg-[#F7F8FA] disabled:cursor-not-allowed disabled:opacity-50'

const MANUAL_METHOD_LABELS: Record<string, string> = {
  card: 'Card',
  vcc: 'Virtual card',
  ach_eft: 'ACH / EFT',
  check: 'Check',
}

function cardLabel(status: {
  cardBrand?: string
  cardLast4?: string
  manualMethod?: string
}): string | null {
  if (status.cardBrand && status.cardLast4) {
    const brand = status.cardBrand.charAt(0).toUpperCase() + status.cardBrand.slice(1)
    return `${brand} •••• ${status.cardLast4}`
  }
  return status.manualMethod
    ? (MANUAL_METHOD_LABELS[status.manualMethod] ?? status.manualMethod)
    : null
}

export function VisitCoPayPanel({ encounterId, selfPay = false }: Props) {
  // A self-pay visit carries only the self-pay fee (no picker); on an insured visit the
  // picker's choice wins over the co-pay default. The Appointment (and with it `selfPay`)
  // arrives after the first render in both mounts, so the kind is derived, not stored.
  const [kindOverride, setKindOverride] = useState<PatientInvoiceKind | null>(null)
  const kind: PatientInvoiceKind = selfPay ? 'self-pay' : (kindOverride ?? 'copay')
  const { statusQuery, createCheckout, sendLink, syncStatus, refund, resendReceipt } = useCopay(
    encounterId,
    kind,
  )
  const [amount, setAmount] = useState('')
  const [copied, setCopied] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)
  const [smsOpen, setSmsOpen] = useState(false)
  const [phone, setPhone] = useState('')

  const status = statusQuery.data
  const label = PATIENT_INVOICE_KIND_LABELS[kind]
  const title = selfPay ? 'Visit Payment' : `Visit ${label}`
  const stop = (e: React.MouseEvent) => e.stopPropagation()

  // Seed the SMS field from the patient's number on file, while leaving it
  // editable — staff routinely text a number the chart does not have (a mobile
  // when the record holds a landline). Only seeded while the popover is closed,
  // so a status refetch cannot overwrite what is being typed.
  useEffect(() => {
    if (!smsOpen && status?.patientPhone) {
      setPhone(formatPhoneInput(status.patientPhone))
    }
  }, [smsOpen, status?.patientPhone])

  // A draft carries the amount from the EOB — prefill it once per kind, and only
  // while the field is empty so a refetch never overwrites what is being typed.
  useEffect(() => {
    if (amount === '' && status?.status === 'draft' && status.amountCents != null) {
      setAmount(String(status.amountCents / 100))
    }
    // Prefill only — user edits must not be overwritten by refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, status?.status, status?.amountCents])

  if (!encounterId) {
    return null
  }

  const notifyError = (error: unknown, fallback: string) =>
    showNotification({ color: 'red', message: errorMessage(error, fallback) })

  const kindSelector = selfPay ? null : (
    <SegmentedControl
      size="xs"
      fullWidth
      value={kind}
      onChange={(value) => {
        setKindOverride(value as PatientInvoiceKind)
        setAmount('')
      }}
      data={EOB_RESPONSIBILITY_KINDS.map((value) => ({
        value,
        label: PATIENT_INVOICE_KIND_LABELS[value],
      }))}
      className="mb-3"
    />
  )

  if (!status) {
    return (
      <DetailCard icon={<IconCoin size={17} stroke={2} />} title={title}>
        {kindSelector}
        <CardSkeleton rows={2} />
      </DetailCard>
    )
  }

  // Awaiting payment — QR + link, patient pays on their own device.
  if (status.status === 'awaiting') {
    const link = status.checkoutUrl ?? ''
    const copyLink = async () => {
      if (!link) return
      try {
        await navigator.clipboard.writeText(link)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      } catch (error) {
        notifyError(error, 'Unable to copy the payment link.')
      }
    }
    const emailLink = async () => {
      try {
        const result = await sendLink.mutateAsync({ encounterId, kind, channel: 'email' })
        showNotification({ color: 'green', message: `Payment link emailed to ${result.sentTo}` })
      } catch (error) {
        notifyError(error, 'Unable to email the payment link.')
      }
    }
    const smsLink = async () => {
      try {
        const result = await sendLink.mutateAsync({
          encounterId,
          kind,
          channel: 'sms',
          phone: normalizePhoneToE164(phone),
        })
        setSmsOpen(false)
        showNotification({ color: 'green', message: `Payment link texted to ${result.sentTo}` })
      } catch (error) {
        notifyError(error, 'Unable to text the payment link.')
      }
    }
    // Webhook backstop (and the main path in local dev, where Stripe webhooks
    // can't reach the middleware): force-reconcile the Invoice with Stripe.
    const checkStatus = async () => {
      try {
        await syncStatus.mutateAsync({ encounterId, kind })
      } catch (error) {
        notifyError(error, 'Unable to check the payment status.')
      }
    }

    return (
      <>
        <DetailCard
          icon={<IconCoin size={17} stroke={2} />}
          title={title}
          badge={<CardBadge label="Awaiting payment" tone="coral" />}
        >
          {kindSelector}
          <div className="flex flex-col gap-3">
            <div className="-mt-1.5 text-[11.5px] font-medium whitespace-nowrap text-[#6B7280]">
              Patient pays on their own device
            </div>
            <div className="flex gap-3.5">
              <div className="flex shrink-0 flex-col items-center gap-1">
                <button
                  type="button"
                  disabled={!link}
                  onClick={() => setQrOpen(true)}
                  aria-label="Enlarge QR code"
                  className="rounded-lg border border-[#E7E9EC] bg-white p-1.5 transition hover:border-[#2C5FE0] enabled:cursor-zoom-in disabled:cursor-default"
                >
                  {link ? (
                    <QRCodeSVG value={link} size={92} />
                  ) : (
                    <div className="flex h-[92px] w-[92px] items-center justify-center text-[11px] text-[#9AA0A8]">
                      No link
                    </div>
                  )}
                </button>
                <span className="text-[11px] text-[#9AA0A8]">Scan to pay</span>
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[24px] leading-none font-extrabold text-[#1F2733]">
                    {dollars(status.amountCents ?? 0)}
                  </span>
                  <span className="text-[12.5px] text-[#9AA0A8]">due</span>
                </div>
                <div className="flex min-w-0 items-center gap-1.5 text-[11.5px] font-bold text-[#E5533D]">
                  <IconLink size={13} stroke={2} className="shrink-0" />
                  {status.linkSentTo ? (
                    <>
                      <span className="shrink-0">Payment link sent to</span>
                      <Tooltip label={status.linkSentTo} withArrow>
                        <span className="min-w-0 truncate">{status.linkSentTo}</span>
                      </Tooltip>
                    </>
                  ) : (
                    <span>Payment link created</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    readOnly
                    value={link}
                    aria-label="Payment link"
                    className="min-w-0 flex-1 rounded-lg border border-[#E7E9EC] bg-[#F7F8FA] px-2.5 py-1.5 text-[12px] text-[#697386] outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => void copyLink()}
                    aria-label="Copy payment link"
                    className={ICON_BUTTON}
                  >
                    {copied ? (
                      <IconCheck size={15} stroke={2} />
                    ) : (
                      <IconCopy size={15} stroke={2} />
                    )}
                  </button>
                  <Tooltip
                    label="Patient has no email on file"
                    disabled={Boolean(status.patientEmail)}
                  >
                    <button
                      type="button"
                      onClick={() => void emailLink()}
                      disabled={sendLink.isPending || !status.patientEmail}
                      aria-label="Email payment link"
                      className={ICON_BUTTON}
                    >
                      <IconMail size={15} stroke={2} />
                    </button>
                  </Tooltip>
                  {/* Text the link. The number is editable rather than taken
                      straight from the chart; the popover exists only because
                      the icon row has no space for a field. */}
                  <Popover
                    opened={smsOpen}
                    onChange={setSmsOpen}
                    position="bottom-end"
                    withArrow
                    shadow="md"
                    radius="md"
                    zIndex={400}
                    trapFocus
                  >
                    <Popover.Target>
                      <button
                        type="button"
                        onClick={() => setSmsOpen((open) => !open)}
                        aria-label="Text payment link"
                        aria-expanded={smsOpen}
                        className={ICON_BUTTON}
                      >
                        <IconMessage size={15} stroke={2} />
                      </button>
                    </Popover.Target>
                    <Popover.Dropdown onClick={stop}>
                      <div className="flex flex-col gap-2">
                        <span className="text-[11.5px] font-bold text-[#4A5160]">
                          Text payment link to
                        </span>
                        <div className="flex items-center gap-1.5">
                          <TextInput
                            value={phone}
                            onChange={(event) =>
                              setPhone(formatPhoneInput(event.currentTarget.value))
                            }
                            placeholder="(818) 555-0123"
                            size="xs"
                            className="w-[150px]"
                            aria-label="Patient phone for SMS"
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' && isPhoneReady(phone)) {
                                event.preventDefault()
                                void smsLink()
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => void smsLink()}
                            disabled={!isPhoneReady(phone) || sendLink.isPending}
                            className="flex h-[30px] shrink-0 items-center gap-1 rounded-lg border border-[#E7E9EC] px-2.5 text-[12px] font-bold text-[#6B7280] hover:bg-[#F7F8FA] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <IconSend size={13} stroke={2} />
                            Send
                          </button>
                        </div>
                      </div>
                    </Popover.Dropdown>
                  </Popover>
                  <Tooltip label="Check payment status with Stripe">
                    <button
                      type="button"
                      onClick={() => void checkStatus()}
                      disabled={syncStatus.isPending}
                      aria-label="Check payment status"
                      className={ICON_BUTTON}
                    >
                      <IconRefresh
                        size={15}
                        stroke={2}
                        className={syncStatus.isPending ? 'animate-spin' : undefined}
                      />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </DetailCard>
        <Modal
          opened={qrOpen}
          onClose={() => setQrOpen(false)}
          title="Scan to pay"
          centered
          radius="lg"
          size="auto"
          // Sit above the schedule's preview popover (z-index 300) that can host the card.
          zIndex={400}
        >
          <div onClick={stop} className="flex flex-col items-center gap-3 p-2">
            {link && <QRCodeSVG value={link} size={280} />}
            <span className="max-w-[300px] text-center text-[12px] break-all text-[#6B7280]">
              {link}
            </span>
          </div>
        </Modal>
      </>
    )
  }

  // Settled — what was collected, the receipt, and the refund.
  if (status.status === 'paid' || status.status === 'refunded') {
    const settled = status.status === 'paid'

    const resend = async () => {
      try {
        const result = await resendReceipt.mutateAsync({ encounterId, kind })
        showNotification({ color: 'green', message: `Receipt re-sent to ${result.sentTo}` })
      } catch (error) {
        notifyError(error, 'Unable to re-send the receipt.')
      }
    }

    const confirmRefund = () => {
      modals.openConfirmModal({
        title: status.manual ? 'Mark as refunded' : `Refund ${label.toLowerCase()}`,
        children: status.manual ? (
          <Text size="sm">
            Mark this {dollars(status.amountCents)} {label.toLowerCase()} as refunded? It was
            recorded manually — no Stripe refund will be made.
          </Text>
        ) : (
          <Text size="sm">
            Refund the full {dollars(status.amountCents)} {label.toLowerCase()} to the
            patient&apos;s card? Stripe will email the refund receipt to the patient. This encounter
            cannot be charged again.
          </Text>
        ),
        labels: status.manual
          ? { confirm: 'Mark as refunded', cancel: 'Cancel' }
          : { confirm: 'Refund', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        // The card can be mounted inside the schedule's preview popover
        // (z-index 300); without this the confirm dialog opens behind it.
        zIndex: 400,
        onConfirm: () => {
          void (async () => {
            try {
              await refund.mutateAsync({ encounterId, kind })
              showNotification({ color: 'green', message: `${label} refunded in full.` })
            } catch (error) {
              notifyError(error, `Unable to refund the ${label.toLowerCase()}.`)
            }
          })()
        },
      })
    }

    const receiptButtons = (
      <>
        <ActionButton
          icon={<IconDownload size={14} />}
          label="Download invoice"
          disabled={!status.receiptUrl}
          onClick={() => window.open(status.receiptUrl, '_blank', 'noopener')}
        />
        <Tooltip label="Patient has no email on file" disabled={Boolean(status.patientEmail)}>
          <span className="flex flex-1">
            <ActionButton
              icon={<IconMail size={14} />}
              label="Resend to patient"
              disabled={!status.patientEmail || resendReceipt.isPending}
              onClick={() => void resend()}
            />
          </span>
        </Tooltip>
      </>
    )

    return (
      <DetailCard
        icon={<IconCoin size={17} stroke={2} />}
        title={title}
        badge={
          settled ? (
            <CardBadge label={`Paid ${dollars(status.amountCents)}`} tone="green" />
          ) : (
            <CardBadge label={`Refunded ${dollars(status.amountCents)}`} tone="gray" />
          )
        }
      >
        {kindSelector}
        <div className="flex flex-col gap-2 text-[12.5px] text-[#6B7280]">
          {kind === 'self-pay' && (
            <>
              <div className="flex justify-between">
                <span>Payment type</span>
                <span className="font-semibold text-[#1F2733]">Self Pay</span>
              </div>
              <div className="flex justify-between">
                <span>Status</span>
                <span>
                  {settled
                    ? status.manual
                      ? 'Recorded manually'
                      : 'Processed by Stripe'
                    : 'Refunded'}
                </span>
              </div>
              {cardLabel(status) && (
                <div className="flex justify-between">
                  <span>Method</span>
                  <span>{cardLabel(status)}</span>
                </div>
              )}
              {settled && (
                <div className="flex justify-between">
                  <span>Remaining balance</span>
                  <span className="font-semibold text-[#1F2733]">$0.00</span>
                </div>
              )}
            </>
          )}
          <div>
            Collected {status.paidAt ? formatDateTime(status.paidAt, '-', true, true) : '-'}
          </div>
          {!settled && (
            <div>
              Refunded{' '}
              {status.refundedAt ? formatDateTime(status.refundedAt, '-', true, true) : '-'}
            </div>
          )}
          {!status.manual && status.patientEmail && (
            <div className="flex items-center gap-1.5 text-[#1A8245]">
              <IconSend size={14} />
              {settled ? 'Receipt' : 'Refund receipt'} emailed to {status.patientEmail} by Stripe
            </div>
          )}
          {(settled || !status.manual) && (
            <div className="mt-1 flex flex-wrap gap-2">
              {!status.manual && receiptButtons}
              {settled && (
                <ActionButton
                  icon={<IconReceiptRefund size={14} />}
                  label="Refund"
                  coral
                  disabled={refund.isPending}
                  onClick={confirmRefund}
                />
              )}
            </div>
          )}
        </div>
      </DetailCard>
    )
  }

  // Collect — none, expired, or a draft whose amount came from the EOB.
  const amountCents = Math.round(Number(amount || 0) * 100)
  // Stripe minimum charge is $0.50.
  const amountValid = Number.isFinite(amountCents) && amountCents >= 50

  const collectNow = async () => {
    // Open the tab synchronously, within the click, so it keeps the user
    // activation — a window.open() that runs only after the await resolves is
    // blocked by browsers. We then point the pre-opened tab at the checkout URL
    // once it's ready (severing `opener` for the noopener security benefit).
    const checkoutTab = window.open('about:blank', '_blank')
    if (checkoutTab) checkoutTab.opener = null
    try {
      const result = await createCheckout.mutateAsync({ encounterId, kind, amountCents })
      if (checkoutTab) checkoutTab.location.href = result.checkoutUrl
      else window.open(result.checkoutUrl, '_blank', 'noopener')
    } catch (error) {
      checkoutTab?.close()
      notifyError(error, 'Unable to start the Stripe checkout.')
    }
  }
  // Creates the link and flips status to awaiting — the card then shows QR/copy/send.
  const sendPaymentLink = async () => {
    try {
      await createCheckout.mutateAsync({ encounterId, kind, amountCents })
    } catch (error) {
      notifyError(error, 'Unable to create the payment link.')
    }
  }

  return (
    <DetailCard
      icon={<IconCoin size={17} stroke={2} />}
      title={title}
      subtitle="Collect without leaving this screen"
      badge={
        status.status === 'draft' ? (
          <CardBadge label={`Suggested: ${dollars(status.amountCents)}`} tone="blue" />
        ) : kind === 'self-pay' && selfPay ? (
          <CardBadge label="Not paid" tone="coral" />
        ) : undefined
      }
    >
      {kindSelector}
      <div className="flex flex-col gap-2.5">
        {status.status === 'expired' && (
          <div className="rounded-lg bg-[#FEF3E7] px-2.5 py-1.5 text-[11.5px] font-medium text-[#B45309]">
            The payment link expired before it was paid — set the amount and send a new one.
          </div>
        )}
        {status.status === 'draft' && (
          <div className="rounded-lg bg-[#E7EEFB] px-2.5 py-1.5 text-[11.5px] font-medium text-[#2C5FE0]">
            Amount from the EOB — editable until a link is created.
          </div>
        )}
        <div className="flex items-stretch overflow-hidden rounded-lg border border-[#E7E9EC]">
          <span className="flex items-center justify-center bg-[#F1F3F7] px-2.5 text-[12px] font-bold text-[#6B7280]">
            $
          </span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.currentTarget.value)}
            placeholder="0"
            aria-label={`${label} amount`}
            className="min-w-0 flex-1 px-2.5 !py-1 !text-[12px] font-semibold text-[#1F2733] outline-none placeholder:text-[#B4B9C0]"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!amountValid || createCheckout.isPending}
            onClick={() => void sendPaymentLink()}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-[#E7E9EC] bg-white px-2.5 py-[3px] !text-[12px] !font-bold whitespace-nowrap text-[#6B7280] hover:bg-[#F7F8FA] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <IconSend size={13} stroke={2} />
            Send payment link
          </button>
          <button
            type="button"
            disabled={!amountValid || createCheckout.isPending}
            onClick={() => void collectNow()}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#2C5FE0] px-2.5 py-[3px] !text-[12px] !font-bold whitespace-nowrap text-white hover:bg-[#254FC0] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <IconCoin size={13} stroke={2.2} />
            Collect now
          </button>
        </div>
      </div>
    </DetailCard>
  )
}
